import { useEffect, useRef } from 'react';

/** Bytes in one megabyte, as the size caps are authored and displayed. */
const BYTES_PER_MB = 1024 * 1024;

/** One slot's claim on a section's combined budget. */
export type BudgetEntry = {
  /** Stable per SLOT, not per file — see {@link useSizeBudget}. */
  key: string;
  bytes: number;
};

export type SizeBudget = {
  /** The cap being enforced, for the refusal message and the heading line. Null = none. */
  capMb: number | null;
  /**
   * Charges `bytes` to `slotKey`, releasing whatever that key already held, and answers whether
   * the result fits. Returns false and charges NOTHING when it would not, so a refused file
   * leaves the budget exactly as it found it.
   */
  tryReserve: (slotKey: string, bytes: number) => boolean;
};

/**
 * Whether one more claim fits, given what is already held.
 *
 * <p>
 * Pulled out as a pure function so the arithmetic — which is the part that is easy to get
 * subtly wrong — is testable without rendering anything.
 * </p>
 *
 * <p>
 * `slotKey`'s existing claim is RELEASED before weighing, because a reservation is per slot and a
 * slot holds one file at a time. See {@link useSizeBudget} for why that matters.
 * </p>
 */
export function fitsWithin(
  held: ReadonlyMap<string, number>,
  slotKey: string,
  bytes: number,
  capBytes: number,
): boolean {
  let total = bytes;
  for (const [key, value] of held) {
    if (key !== slotKey) total += value;
  }
  return total <= capBytes;
}

/**
 * A combined-size budget for one section of the document-upload step, enforced at pick time.
 *
 * <p>
 * Three problems have to be solved together, and solving any two of them is what makes a naive
 * running total wrong:
 * </p>
 *
 * <h3>1. Props are stale mid-drop</h3>
 * <p>
 * A multi-file drop runs `beforeUpload` once per file BEFORE React re-renders, so a total computed
 * from props is the total as the drop began — every file in the drop would be weighed against an
 * empty section and all of them would pass. The running figure therefore lives in a ref. This is
 * the same hazard `OptionalDocumentsBucket`'s `committedCount` ref already exists for, and the
 * same shape of answer.
 * </p>
 *
 * <h3>2. Removals have to give the budget back</h3>
 * <p>
 * The ref is resynced from the committed list on every render, keyed on a SIGNATURE string rather
 * than the array itself — the array is rebuilt each render, so an identity-keyed effect would fire
 * forever. (`AdditionalDocumentsStep` uses the same trick for its blocker memo.)
 * </p>
 *
 * <h3>3. A named entry REPLACES its file rather than appending</h3>
 * <p>
 * This is why the ledger is a map and not a number. Dropping two files on one empty entry commits
 * the first and then replaces it with the second, both before a render — a scalar counter charges
 * the entry twice and refuses the second file against budget the first one is about to vacate. A
 * reservation keyed on the slot releases the previous claim as it makes the new one, so the entry
 * is only ever charged for the file it actually holds. The bucket, which genuinely appends, passes
 * each new file's own uid and so accumulates normally through the same code.
 * </p>
 *
 * @param capMb The configured cap, or null/undefined when the originator authored none — in which
 *   case `tryReserve` always succeeds. That is what keeps every step older than this setting
 *   behaving exactly as it did.
 * @param committed What the section currently holds, from the store.
 */
export function useSizeBudget(
  capMb: number | null | undefined,
  committed: readonly BudgetEntry[],
): SizeBudget {
  const held = useRef(new Map<string, number>());

  const signature = committed.map((c) => `${c.key}:${c.bytes}`).join('|');
  useEffect(() => {
    held.current = new Map(committed.map((c) => [c.key, c.bytes]));
    // Read through the signature above: `committed` is a fresh array on every render, so listing
    // it here would re-run this effect indefinitely.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  const cap = capMb ?? null;
  return {
    capMb: cap,
    tryReserve: (slotKey, bytes) => {
      if (cap === null) return true;
      if (!fitsWithin(held.current, slotKey, bytes, cap * BYTES_PER_MB)) return false;
      held.current.set(slotKey, bytes);
      return true;
    },
  };
}
